/**
 * Strings of stage 5 "Teren i logistyka" (`teren.*`): Logistyka → Raporty (XLSX pack + daily e-mail), the customer tab
 * „Menu i ankieta”, the customer list filter and the organization setting for AI menu reading. `terenEn` is typed against `terenPl`.
 */
type Shape<T> = { [K in keyof T]: T[K] extends string ? string : Shape<T[K]> };

export const terenPl = {
  reports: {
    view: 'Raporty',
    title: 'Raporty logistyki',
    subtitle: 'Paczka raportów w jednym pliku Excel i cykliczna wysyłka e-mailem',
    packTitle: 'Paczka raportów (XLSX)',
    packHint: 'Jeden plik z pięcioma zakładkami, w zakresie danych, które widzisz w NEXOPS. Najwyżej 31 dni.',
    from: 'Od',
    to: 'Do',
    presets: { yesterday: 'Wczoraj', today: 'Dziś', last7: 'Ostatnie 7 dni', month: 'Bieżący miesiąc' },
    download: 'Pobierz paczkę',
    downloading: 'Przygotowuję plik…',
    downloaded: 'Pobrano {name}',
    downloadFailed: 'Nie udało się przygotować paczki',
    rangeInvalid: 'Zakres musi mieć od 1 do 31 dni',
    sheetsTitle: 'Zakładki w pliku',
    sheets: {
      routes: { name: 'Trasy dzienne', hint: 'Trasa, kierowca, pojazd, liczba dostaw i punktów, km, godzina startu i końca, czas pracy' },
      drivers: { name: 'Dostawy wg kierowców', hint: 'Dni pracy, kursy, dostawy, nieudane, spóźnione, terminowość, km, godziny' },
      problems: { name: 'Nieudane i spóźnione', hint: 'Każda dostawa z problemem: klient, WZ, kierowca, przyczyna, dni opóźnienia' },
      complaints: { name: 'Zgłoszenia i reklamacje', hint: 'Zgłoszenia kierowców (awarie, zdarzenia) i reklamacje z dostaw' },
      loading: { name: 'Załadunek pojazdów', hint: 'Punkty, dokumenty WZ, pozycje i ilości (kg, szt., opak.) na trasę' },
    },
    sourcesNote: 'Km pochodzą z karty drogowej, a bez niej z kosztu trasy (szacunek) lub planu Routimo — źródło jest w osobnej kolumnie.',
    mail: {
      title: 'Wysyłka e-mailem',
      hint: 'Codziennie o wybranej godzinie paczka za poprzedni dzień trafia do odbiorców. Każdy dostaje plik w swoim zakresie danych.',
      enabled: 'Wysyłaj codziennie',
      hour: 'Godzina wysyłki',
      hourOption: '{hour}:00',
      recipients: 'Odbiorcy',
      recipientsHint: 'Tylko aktywni użytkownicy z dostępem do logistyki.',
      noOptions: 'Brak użytkowników z dostępem do logistyki',
      lastSent: 'Ostatnia wysyłka: {date}',
      neverSent: 'Jeszcze nie wysłano',
      save: 'Zapisz ustawienia',
      saved: 'Zapisano ustawienia wysyłki',
      saveFailed: 'Nie udało się zapisać ustawień',
      sendNow: 'Wyślij za wczoraj teraz',
      sent: 'Wysłano {sent} z {recipients} wiadomości',
      sendFailed: 'Nie udało się wysłać paczki',
      readOnly: 'Ustawienia wysyłki zmienia osoba z uprawnieniem do edycji logistyki.',
    },
  },
  profile: {
    tab: 'Menu i ankieta',
    demo: 'dane przykładowe',
    menu: {
      title: 'Menu restauracji',
      hint: 'Link do publicznego menu — przypomnienie dla handlowca przed wizytą (karta klienta, plan wizyty).',
      none: 'Brak linku do menu',
      open: 'Otwórz menu',
      edit: 'Zmień link',
      add: 'Dodaj link',
      placeholder: 'https://…',
      save: 'Zapisz',
      cancel: 'Anuluj',
      remove: 'Usuń link',
      saved: 'Zapisano link do menu',
      removed: 'Usunięto link do menu',
      saveFailed: 'Nie udało się zapisać linku',
      summaryTitle: 'Streszczenie menu (AI)',
      summarize: 'Streść menu',
      summarizing: 'Przygotowuję streszczenie…',
      summaryAt: 'Przygotowano {date} · {model}',
      summaryFailed: 'Nie udało się przygotować streszczenia',
      aiOff: 'Streszczenie przez AI jest wyłączone w ustawieniach organizacji.',
    },
    survey: {
      title: 'Zgody i faktury',
      hint: 'Ankieta „Zgody i faktury” wypełniana w wizycie w aplikacji handlowca; dane z Subiekta (InsERT) uzupełniają odpowiedzi.',
      none: 'Ankieta nie została jeszcze wypełniona',
      marketing: 'Zgoda na kontakt marketingowy',
      channels: 'Kanał powiadomień',
      invoice: 'Faktura',
      filled: 'Data wypełnienia',
      filledBy: 'Wypełnił(a)',
      yes: 'Tak',
      no: 'Nie',
      empty: '—',
      channel: { SMS: 'SMS', EMAIL: 'E-mail', PHONE: 'Telefon' },
      invoiceFormat: { ELECTRONIC: 'Elektroniczna', PAPER: 'Papierowa' },
      effective: 'Faktura (obowiązująca)',
      source: { SURVEY: 'z ankiety', ERP: 'z ERP' },
      erpTitle: 'Zgody w ERP (InsERT)',
      erpNone: 'Brak danych z ERP',
      erpEInvoice: 'Zgoda na e-faktury',
      erpEInvoiceSince: 'od {date}',
      erpMarketing: 'Zgoda marketingowa',
      erpEmail: 'Zgoda na e-mail',
      erpSynced: 'Odczyt z ERP: {date}',
    },
    settings: {
      section: 'Menu restauracji',
      sectionHint: 'Czy AI może czytać publiczne strony menu klientów',
      aiLabel: 'AI może czytać publiczne strony menu',
      aiHint: 'Domyślnie wyłączone. Po włączeniu handlowiec może poprosić o streszczenie menu w karcie klienta. NEXOPS pobiera wtedy tekst strony spod linku (tylko publiczne adresy) i przekazuje go AI — bez nazwy klienta i innych danych. W trybie demonstracyjnym strony nie są pobierane.',
      saved: 'Zapisano ustawienie',
      saveFailed: 'Nie udało się zapisać ustawienia',
    },
  },
  filter: {
    label: 'Ankieta',
    all: 'Ankieta: wszyscy',
    paper: 'Faktura papierowa',
    electronic: 'Faktura elektroniczna',
    mktYes: 'Zgoda marketingowa: tak',
    mktNo: 'Zgoda marketingowa: nie',
    sms: 'Kanał: SMS',
    email: 'Kanał: e-mail',
    phone: 'Kanał: telefon',
    menu: 'Z linkiem do menu',
  },
};

export const terenEn: Shape<typeof terenPl> = {
  reports: {
    view: 'Reports',
    title: 'Logistics reports',
    subtitle: 'Report pack in one Excel file and a scheduled e-mail',
    packTitle: 'Report pack (XLSX)',
    packHint: 'One file with five sheets, limited to the data you can see in NEXOPS. At most 31 days.',
    from: 'From',
    to: 'To',
    presets: { yesterday: 'Yesterday', today: 'Today', last7: 'Last 7 days', month: 'This month' },
    download: 'Download pack',
    downloading: 'Preparing the file…',
    downloaded: 'Downloaded {name}',
    downloadFailed: 'Could not prepare the pack',
    rangeInvalid: 'The range must be 1 to 31 days',
    sheetsTitle: 'Sheets in the file',
    sheets: {
      routes: { name: 'Daily routes', hint: 'Route, driver, vehicle, deliveries and stops, km, start and end time, working time' },
      drivers: { name: 'Deliveries per driver', hint: 'Working days, runs, deliveries, failed, late, on-time rate, km, hours' },
      problems: { name: 'Failed and late', hint: 'Every delivery with a problem: customer, goods issue, driver, reason, days late' },
      complaints: { name: 'Reports and complaints', hint: 'Driver reports (breakdowns, incidents) and delivery complaints' },
      loading: { name: 'Vehicle loading', hint: 'Stops, goods issues, lines and quantities (kg, pcs, packages) per route' },
    },
    sourcesNote: 'Km come from the logbook, otherwise from the route cost (estimate) or the Routimo plan — the source has its own column.',
    mail: {
      title: 'E-mail delivery',
      hint: 'Every day at the chosen hour the pack for the previous day goes to the recipients. Everyone gets a file in their own data scope.',
      enabled: 'Send daily',
      hour: 'Sending hour',
      hourOption: '{hour}:00',
      recipients: 'Recipients',
      recipientsHint: 'Only active users with access to logistics.',
      noOptions: 'No users with access to logistics',
      lastSent: 'Last sent: {date}',
      neverSent: 'Not sent yet',
      save: 'Save settings',
      saved: 'E-mail settings saved',
      saveFailed: 'Could not save the settings',
      sendNow: "Send yesterday's pack now",
      sent: 'Sent {sent} of {recipients} messages',
      sendFailed: 'Could not send the pack',
      readOnly: 'E-mail settings can be changed by a user allowed to edit logistics.',
    },
  },
  profile: {
    tab: 'Menu and survey',
    demo: 'sample data',
    menu: {
      title: 'Restaurant menu',
      hint: 'Link to the public menu — a reminder for the rep before the visit (customer card, visit plan).',
      none: 'No menu link',
      open: 'Open menu',
      edit: 'Change link',
      add: 'Add link',
      placeholder: 'https://…',
      save: 'Save',
      cancel: 'Cancel',
      remove: 'Remove link',
      saved: 'Menu link saved',
      removed: 'Menu link removed',
      saveFailed: 'Could not save the link',
      summaryTitle: 'Menu summary (AI)',
      summarize: 'Summarize menu',
      summarizing: 'Preparing the summary…',
      summaryAt: 'Prepared {date} · {model}',
      summaryFailed: 'Could not prepare the summary',
      aiOff: 'AI summaries are switched off in the organization settings.',
    },
    survey: {
      title: 'Consents and invoices',
      hint: 'The “Consents and invoices” survey filled in during a visit in the rep app; data from Subiekt (InsERT) complements the answers.',
      none: 'The survey has not been filled in yet',
      marketing: 'Marketing contact consent',
      channels: 'Notification channel',
      invoice: 'Invoice',
      filled: 'Filled in',
      filledBy: 'Filled in by',
      yes: 'Yes',
      no: 'No',
      empty: '—',
      channel: { SMS: 'SMS', EMAIL: 'E-mail', PHONE: 'Phone' },
      invoiceFormat: { ELECTRONIC: 'Electronic', PAPER: 'Paper' },
      effective: 'Invoice (in effect)',
      source: { SURVEY: 'from the survey', ERP: 'from the ERP' },
      erpTitle: 'Consents in the ERP (InsERT)',
      erpNone: 'No ERP data',
      erpEInvoice: 'E-invoice consent',
      erpEInvoiceSince: 'since {date}',
      erpMarketing: 'Marketing consent',
      erpEmail: 'E-mail consent',
      erpSynced: 'Read from the ERP: {date}',
    },
    settings: {
      section: 'Restaurant menus',
      sectionHint: 'Whether AI may read public menu pages of customers',
      aiLabel: 'AI may read public menu pages',
      aiHint: 'Off by default. When on, a rep can ask for a menu summary on the customer card. NEXOPS then downloads the text of the linked page (public addresses only) and passes it to AI — without the customer name or any other data. In demo mode pages are not downloaded.',
      saved: 'Setting saved',
      saveFailed: 'Could not save the setting',
    },
  },
  filter: {
    label: 'Survey',
    all: 'Survey: all',
    paper: 'Paper invoice',
    electronic: 'Electronic invoice',
    mktYes: 'Marketing consent: yes',
    mktNo: 'Marketing consent: no',
    sms: 'Channel: SMS',
    email: 'Channel: e-mail',
    phone: 'Channel: phone',
    menu: 'With a menu link',
  },
};
